<template>
  <el-row>
    <el-col>
      <Filters
        class="mb-3"
        :resource="$sdk.account_event"
        :filters.sync="filters"
        @apply="reload"
        @ready="load"
      />

      <el-tabs
        v-loading="isLoading"
        type="border-card"
      >
        <el-tab-pane label="Past Due">
          <PastDue
            :events="pastDueEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="pastDueEvents.page"
            :page-size.sync="pastDueEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pastDueEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-tab-pane>

        <el-tab-pane label="Upcoming">
          <Upcoming
            :events="upcomingEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="upcomingEvents.page"
            :page-size.sync="upcomingEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="upcomingEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-tab-pane>

        <el-tab-pane label="Later On">
          <LaterOn
            :events="laterOnEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="laterOnEvents.page"
            :page-size.sync="laterOnEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="laterOnEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-tab-pane>

        <el-tab-pane label="In The Future">
          <InTheFuture
            :events="inTheFutureEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="inTheFutureEvents.page"
            :page-size.sync="inTheFutureEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="inTheFutureEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-tab-pane>

        <el-tab-pane label="Completed">
          <Completed
            :events="completedEvents"
            @completed-event="reload"
            @deferred-event="reload"
            @created-event="reload"
          />

          <el-pagination
            class="text-center pt-3"
            :current-page.sync="completedEvents.page"
            :page-size.sync="completedEvents.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="completedEvents.total"
            @size-change="reload"
            @current-change="reload"
          />
        </el-tab-pane>
      </el-tabs>
    </el-col>
  </el-row>
</template>

<script>
import Filters from '@/components/Filters/Complex'
import Filter, { IS_LESS_THAN, IS_GREATER_THAN, IS_BETWEEN, IS, IS_NOT } from '@/plugins/sdk/Filter'
import PastDue from '@/components/Application/Sales/ToDo/TradingAccounts/PastDue'
import Upcoming from '@/components/Application/Sales/ToDo/TradingAccounts/Upcoming'
import LaterOn from '@/components/Application/Sales/ToDo/TradingAccounts/LaterOn'
import InTheFuture from '@/components/Application/Sales/ToDo/TradingAccounts/InTheFuture'
import Completed from '@/components/Application/Sales/ToDo/TradingAccounts/Completed'
import { moment } from '@/plugins/moment'

const TODAY = moment().startOf('day')
const TOMORROW = moment(TODAY).nextBusinessDay().endOf('day')
const LATER_ON = moment(TOMORROW).businessAdd(5).endOf('day')

export default {
  components: {
    Filters,
    PastDue,
    Upcoming,
    LaterOn,
    InTheFuture,
    Completed
  },
  data () {
    return {
      isLoadingPastDueEvents: true,
      isLoadingUpcomingEvents: true,
      isLoadingLaterOnEvents: true,
      isLoadingInTheFutureEvents: true,
      isLoadingCompletedEvents: true,
      portfolios: [],
      filters: [
        new Filter('account.status', IS_NOT, [ 'lead', 'pending' ], true, true)
      ],
      pastDueEvents: [],
      upcomingEvents: [],
      laterOnEvents: [],
      inTheFutureEvents: [],
      completedEvents: []
    }
  },
  computed: {
    isLoading () {
      return this.isLoadingPastDueEvents ||
        this.isLoadingUpcomingEvents ||
        this.isLoadingLaterOnEvents ||
        this.isLoadingInTheFutureEvents ||
        this.isLoadingCompletedEvents
    },
    pastDueEventsFilters () {
      return [
        new Filter('completed_at', IS, 'null', true, true),
        new Filter('account.portfolio', IS, [ null, this.portfolios ]),
        new Filter('due_at', IS_LESS_THAN, TODAY),
        ...this.filters
      ]
    },
    upcomingEventsFilters () {
      return [
        new Filter('completed_at', IS, 'null', true, true),
        new Filter('account.portfolio', IS, [ null, this.portfolios ]),
        new Filter('due_at', IS_BETWEEN, [ TODAY, TOMORROW ]),
        ...this.filters
      ]
    },
    laterOnEventsFilters () {
      return [
        new Filter('completed_at', IS, 'null', true, true),
        new Filter('account.portfolio', IS, [ null, this.portfolios ]),
        new Filter('due_at', IS_BETWEEN, [ TOMORROW, LATER_ON ]),
        ...this.filters
      ]
    },
    inTheFutureEventsFilters () {
      return [
        new Filter('completed_at', IS, 'null', true, true),
        new Filter('account.portfolio', IS, [ null, this.portfolios ]),
        new Filter('due_at', IS_GREATER_THAN, LATER_ON),
        ...this.filters
      ]
    },
    completedEventsFilters () {
      return [
        new Filter('completed_at', IS_NOT, 'null', true, true),
        new Filter('account.portfolio', IS, [ null, this.portfolios ]),
        ...this.filters
      ]
    }
  },
  methods: {
    load () {
      this
        .$sdk
        .dictionary_portfolio
        .list(builder => {
          builder.limit = 1000
        })
        .then(portfolios => {
          this.portfolios = this.$_.map(portfolios, 'id')

          Promise
            .all([
              this.getPastDueEvents(),
              this.getUpcomingEvents(),
              this.getLaterOnEvents(),
              this.getInTheFutureEvents(),
              this.getCompletedEvents()
            ])
            .then(results => {
              this.pastDueEvents = results[0]
              this.upcomingEvents = results[1]
              this.laterOnEvents = results[2]
              this.inTheFutureEvents = results[3]
              this.completedEvents = results[4]
            })
        })
    },
    getPastDueEvents () {
      this.isLoadingPastDueEvents = true

      return new Promise(resolve => {
        this
          .$sdk
          .account_event
          .list(request => {
            request.filters = this.pastDueEventsFilters
            request.include = ['type', 'account.portfolio', 'account.lead']
            request.limit = 10
            request.order = 'priority:desc'
          })
          .then(resolve)
          .finally(() => { this.isLoadingPastDueEvents = false })
      })
    },
    getUpcomingEvents () {
      this.isLoadingUpcomingEvents = true

      return new Promise(resolve => {
        this
          .$sdk
          .account_event
          .list(request => {
            request.filters = this.upcomingEventsFilters
            request.include = ['type', 'account.portfolio', 'account.lead']
            request.limit = 10
            request.order = 'priority:desc'
          })
          .then(resolve)
          .finally(() => { this.isLoadingUpcomingEvents = false })
      })
    },
    getLaterOnEvents () {
      this.isLoadingLaterOnEvents = true

      return new Promise(resolve => {
        this
          .$sdk
          .account_event
          .list(request => {
            request.filters = this.laterOnEventsFilters
            request.include = ['type', 'account.portfolio', 'account.lead']
            request.limit = 10
            request.order = 'priority:desc'
          })
          .then(resolve)
          .finally(() => { this.isLoadingLaterOnEvents = false })
      })
    },
    getInTheFutureEvents () {
      this.isLoadingInTheFutureEvents = true

      return new Promise(resolve => {
        this
          .$sdk
          .account_event
          .list(request => {
            request.filters = this.inTheFutureEventsFilters
            request.include = ['type', 'account.portfolio', 'account.lead']
            request.limit = 10
            request.order = 'priority:desc'
          })
          .then(resolve)
          .finally(() => { this.isLoadingInTheFutureEvents = false })
      })
    },
    getCompletedEvents () {
      this.isLoadingCompletedEvents = true

      return new Promise(resolve => {
        this
          .$sdk
          .account_event
          .list(request => {
            request.filters = this.completedEventsFilters
            request.include = ['type', 'account.portfolio', 'account.lead']
            request.limit = 10
            request.order = 'completed_at:desc'
          })
          .then(resolve)
          .finally(() => { this.isLoadingCompletedEvents = false })
      })
    },
    async reload () {
      try {
        if (!this.loading) {
          await Promise.all([
            this.reloadPastDueEvents(),
            this.reloadUpcomingEvents(),
            this.reloadLaterOnEvents(),
            this.reloadInTheFutureEvents(),
            this.reloadCompletedEvents()
          ])
        }
      } catch {
        this.$message.error('Failed to load Events')
      }
    },
    async reloadPastDueEvents () {
      this.isLoadingPastDueEvents = true

      await this.pastDueEvents.reload(request => { request.filters = this.pastDueEventsFilters })

      this.isLoadingPastDueEvents = false
    },
    async reloadUpcomingEvents () {
      this.isLoadingUpcomingEvents = true

      await this.upcomingEvents.reload(request => { request.filters = this.upcomingEventsFilters })

      this.isLoadingUpcomingEvents = false
    },
    async reloadLaterOnEvents () {
      this.isLoadingLaterOnEvents = true

      await this.laterOnEvents.reload(request => { request.filters = this.laterOnEventsFilters })

      this.isLoadingLaterOnEvents = false
    },
    async reloadInTheFutureEvents () {
      this.isLoadingInTheFutureEvents = true

      await this.inTheFutureEvents.reload(request => { request.filters = this.inTheFutureEventsFilters })

      this.isLoadingInTheFutureEvents = false
    },
    async reloadCompletedEvents () {
      this.isLoadingCompletedEvents = true

      await this.completedEvents.reload(request => { request.filters = this.completedEventsFilters })

      this.isLoadingCompletedEvents = false
    }
  }
}
</script>
